/**
 * QRISE Mystery Box — gameplay wrapper.
 *
 * Two flows depending on URL state:
 *   A. No ?mb_code in URL → "participation" mode:
 *        - The box is shown closed/idle behind the participant info form
 *          (handled by PublicGame.jsx itself)
 *        - On submit, the parent flow already collected info + CTAs and
 *          calls submitParticipation() once → backend tirage + code email
 *        - We then show the "Vérifie ta boîte mail" screen with a 6-digit
 *          input fallback so the player can paste the code without leaving.
 *
 *   B. ?mb_code in URL → "unlock" mode:
 *        - We immediately POST /mystery-unlock with the code from the URL
 *        - Animation plays based on is_win
 *        - "Voir mon cadeau" button on win → calls onResult so the existing
 *          claim/email pipeline kicks in (parent handles modal + claim).
 *
 * The component keeps the API surface identical to FlappyMascotte so the
 * dispatch in PublicGame.jsx stays clean: receives `participant`,
 * `ctasCompleted`, `onResult`, `onAlreadyParticipated`.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Sparkles, Gift, KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MysteryBoxAnimation from "./MysteryBoxAnimation";
import { getOrCreateDeviceId } from "@/lib/fingerprint";
import { resolveImageUrl } from "@/lib/images";
import { playerErrorMessage } from "@/lib/playerErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MysteryBox({
  game,
  participant,
  ctasCompleted,
  receipt,
  initialCode,
  onResult,
  onAlreadyParticipated,
  onSubmitted,
}) {
  const { t } = useTranslation();
  // stage:  participating  → submitting  → email_sent  → unlocking → revealed
  const [stage, setStage] = useState(initialCode ? "auto_unlocking" : "participating");
  const [emailSent, setEmailSent] = useState(null);
  const [emailDelivered, setEmailDelivered] = useState(true);
  const [code, setCode] = useState((initialCode || "").trim());
  const [error, setError] = useState("");
  const [unlockResult, setUnlockResult] = useState(null);
  const submitInFlightRef = useRef(false);
  const triggeredAutoUnlockRef = useRef(false);

  // -------- Direct unlock from email link (?mb_code=XXXXXX) ----------------
  useEffect(() => {
    if (!initialCode || triggeredAutoUnlockRef.current) return;
    triggeredAutoUnlockRef.current = true;
    handleUnlock(initialCode).catch(() => {});
  }, [initialCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------- Submission ----------------------------------------------------
  async function handleSubmit() {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setStage("submitting");
    try {
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/mystery-enter`,
        {
          participant,
          ctas_completed: ctasCompleted || [],
          fingerprint: { device_id: getOrCreateDeviceId() },
          receipt_url: receipt?.url,
          receipt_ocr: receipt?.ocr,
        },
      );
      setEmailSent(data.email);
      setEmailDelivered(data.code_sent !== false);
      // Email delivery failed (Resend down, unverified domain, etc.) —
      // pre-fill the code so the player can still open their box.
      if (data.code_sent === false && data.fallback_code) {
        setCode(String(data.fallback_code));
      }
      setStage("email_sent");
      onSubmitted?.();
    } catch (e) {
      const msg = e.response?.data?.detail ? playerErrorMessage(e) : "";
      if (e.response?.status === 409) {
        onAlreadyParticipated?.(msg || t("mystery.already", "Vous avez déjà participé à ce jeu"));
      } else {
        toast.error(msg || t("mystery.submit_error", "Impossible d'envoyer le code par email"));
      }
      setStage("participating");
    } finally {
      submitInFlightRef.current = false;
    }
  }

  // -------- Unlock --------------------------------------------------------
  async function handleUnlock(rawCode) {
    const trimmed = (rawCode || "").trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError(t("mystery.code_format", "Le code doit contenir 6 chiffres."));
      setStage("email_sent");
      return;
    }
    setError("");
    setStage("unlocking");
    try {
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/mystery-unlock`,
        { code: trimmed },
      );
      setUnlockResult(data);
      // Wait a beat so the animation has time to play before potentially
      // bubbling up the result to the parent (parent shows claim modal).
      setStage("revealed");
    } catch (e) {
      setError(e.response?.data?.detail ? playerErrorMessage(e) : t("mystery.code_invalid", "Code invalide"));
      setStage("email_sent");
    }
  }

  // ----- Render -----------------------------------------------------------
  if (stage === "participating") {
    return (
      <IntroCard game={game} onStart={handleSubmit} disabled={false} />
    );
  }
  if (stage === "submitting") {
    return (
      <IntroCard game={game} onStart={() => {}} disabled={true} />
    );
  }
  if (stage === "email_sent") {
    return (
      <EmailSentCard
        email={emailSent}
        code={code}
        setCode={setCode}
        onUnlock={() => handleUnlock(code)}
        error={error}
        emailDelivered={emailDelivered}
      />
    );
  }
  if (stage === "auto_unlocking" || stage === "unlocking") {
    return (
      <UnlockingCard
        boxStyle={game.mystery_box_style}
        message={t("mystery.unlocking", "Ouverture de la boîte…")}
      />
    );
  }
  // revealed
  return (
    <RevealedCard
      game={game}
      result={unlockResult}
      onClaim={() => onResult?.(unlockResult)}
      onRetry={() => {
        setError("");
        setCode("");
        setUnlockResult(null);
        setStage(initialCode ? "auto_unlocking" : "email_sent");
      }}
    />
  );
}

// ======= Sub-views =======================================================

function IntroCard({ game, onStart, disabled }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-md rounded-[28px] border border-white/10 p-6 sm:p-8 text-center backdrop-blur-2xl"
      style={{
        background: "linear-gradient(180deg, rgba(18,10,34,0.85) 0%, rgba(5,6,12,0.92) 100%)",
        boxShadow: "0 40px 100px -40px rgba(168,139,250,0.55)",
      }}
      data-testid="mystery-intro"
    >
      <MysteryBoxAnimation styleKey={game.mystery_box_style} state="idle" />
      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] uppercase tracking-[0.4em] font-bold text-white/70">
        <Gift className="h-3 w-3" /> {t("mystery.eyebrow", "Boîte mystère")}
      </div>
      <h2 className="mt-3 text-2xl font-extrabold text-white">
        {t("mystery.intro_title", "Qu'y a-t-il dans la boîte ?")}
      </h2>
      <p className="mt-2 text-[13px] text-white/70 leading-relaxed">
        {t(
          "mystery.intro_text",
          "Participe maintenant pour recevoir ton code à 6 chiffres par email. Avec ce code, tu pourras tenter d'ouvrir la boîte et découvrir si elle te réserve un cadeau.",
        )}
      </p>
      <Button
        type="button"
        onClick={onStart}
        disabled={disabled}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-500 via-pink-500 to-amber-400 px-6 py-3 text-sm font-bold text-white shadow-xl hover:brightness-110 disabled:opacity-50"
        data-testid="mystery-participate-btn"
      >
        {disabled ? (
          <>
            <Sparkles className="h-4 w-4 animate-pulse" />
            {t("mystery.sending", "Envoi du code…")}
          </>
        ) : (
          <>
            <Mail className="h-4 w-4" />
            {t("mystery.participate", "Recevoir mon code par email")}
          </>
        )}
      </Button>
    </motion.div>
  );
}

function EmailSentCard({ email, code, setCode, onUnlock, error, emailDelivered = true }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-md rounded-[28px] border border-white/10 p-6 sm:p-8 text-center backdrop-blur-2xl"
      style={{
        background: "linear-gradient(180deg, rgba(18,10,34,0.85) 0%, rgba(5,6,12,0.92) 100%)",
        boxShadow: "0 40px 100px -40px rgba(168,139,250,0.55)",
      }}
      data-testid="mystery-email-sent"
    >
      <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center border ${emailDelivered ? "bg-emerald-500/15 border-emerald-400/30" : "bg-amber-500/15 border-amber-400/30"}`}>
        {emailDelivered ? (
          <Mail className="h-7 w-7 text-emerald-300" />
        ) : (
          <KeyRound className="h-7 w-7 text-amber-300" />
        )}
      </div>
      <h2 className="mt-4 text-2xl font-extrabold text-white">
        {emailDelivered
          ? t("mystery.sent_title", "Vérifie ta boîte mail !")
          : t("mystery.fallback_title", "Voici ton code")}
      </h2>
      {emailDelivered ? (
        <>
          <p className="mt-2 text-[13px] text-white/70 leading-relaxed">
            {t(
              "mystery.sent_text",
              "Nous avons envoyé un email contenant ton code à 6 chiffres à {{email}}. Clique sur le bouton dans l'email pour ouvrir ta boîte.",
              { email: email || t("mystery.your_address") },
            )}
          </p>
          <p className="mt-1 text-[11px] text-white/50">
            {t("mystery.sent_spam_hint", "Pense à vérifier le dossier spam si tu ne le vois pas.")}
          </p>
        </>
      ) : (
        <p className="mt-2 text-[13px] text-amber-200/90 leading-relaxed">
          {t(
            "mystery.fallback_text",
            "Nous n'avons pas pu envoyer l'email à {{email}}, mais voici quand même ton code. Note-le, tu en auras besoin pour rouvrir la boîte.",
            { email: email || t("mystery.your_address") },
          )}
        </p>
      )}

      <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4">
        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-white/60 flex items-center justify-center gap-1.5">
          <KeyRound className="h-3 w-3" />
          {emailDelivered
            ? t("mystery.enter_manually", "Saisir le code manuellement")
            : t("mystery.your_code", "Ton code à 6 chiffres")}
        </div>
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="• • • • • •"
          className="mt-3 h-14 text-center text-2xl tracking-[0.6em] font-black bg-black/30 border-white/10 text-white"
          data-testid="mystery-code-input"
        />
        {error && (
          <div className="mt-2 text-xs text-red-300 font-semibold" data-testid="mystery-code-error">
            {error}
          </div>
        )}
        <Button
          type="button"
          onClick={onUnlock}
          disabled={code.length !== 6}
          className="mt-3 w-full rounded-xl bg-gradient-to-r from-violet-500 via-pink-500 to-amber-400 text-white font-bold disabled:opacity-50"
          data-testid="mystery-unlock-btn"
        >
          {t("mystery.unlock_btn", "Ouvrir ma boîte")}
        </Button>
      </div>
    </motion.div>
  );
}

function UnlockingCard({ boxStyle, message }) {
  return (
    <div
      className="mx-auto max-w-md rounded-[28px] border border-white/10 p-6 sm:p-8 text-center backdrop-blur-2xl"
      style={{
        background: "linear-gradient(180deg, rgba(18,10,34,0.85) 0%, rgba(5,6,12,0.92) 100%)",
        boxShadow: "0 40px 100px -40px rgba(168,139,250,0.55)",
      }}
      data-testid="mystery-unlocking"
    >
      <MysteryBoxAnimation styleKey={boxStyle} state="idle" />
      <div className="mt-4 text-sm text-white/70 animate-pulse">{message}</div>
    </div>
  );
}

function RevealedCard({ game, result, onClaim, onRetry }) {
  const { t } = useTranslation();
  const isWin = !!result?.is_win;
  const replay = !!result?.replay;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="mx-auto max-w-md rounded-[28px] border border-white/10 p-6 sm:p-8 text-center backdrop-blur-2xl"
      style={{
        background: "linear-gradient(180deg, rgba(18,10,34,0.85) 0%, rgba(5,6,12,0.92) 100%)",
        boxShadow: "0 40px 100px -40px rgba(168,139,250,0.55)",
      }}
      data-testid="mystery-revealed"
    >
      <MysteryBoxAnimation
        styleKey={game.mystery_box_style}
        state={isWin ? "opening" : "shaking"}
        prizeLabel={isWin ? result.result_label : null}
        prizeColor={result?.result_color}
        prizeImage={isWin ? resolveImageUrl(result?.result_image) : null}
      />

      {replay && (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.32em] font-bold text-white/60">
          ⏱ {t("mystery.replay", "Tu as déjà ouvert cette boîte")}
        </div>
      )}

      {isWin ? (
        <>
          <div className="mt-5 text-xs uppercase tracking-[0.4em] font-bold text-emerald-300">
            {t("mystery.win_eyebrow", "Tu as gagné")}
          </div>
          <div className="mt-2 text-2xl font-extrabold text-white">
            {result.result_label}
          </div>
          {result.popup_info && (
            <div
              className="mt-4 rounded-2xl border border-sky-300/30 bg-sky-500/10 p-3.5 text-left"
              data-testid="mystery-popup-info"
            >
              <div className="flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-sky-200">
                  {t("play.popup_info_title", "Détails supplémentaires")}
                </div>
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-white/85 whitespace-pre-line">
                {result.popup_info}
              </p>
            </div>
          )}
          <Button
            type="button"
            onClick={onClaim}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-400 via-cyan-400 to-violet-400 px-6 py-3 text-sm font-bold text-black hover:brightness-110"
            data-testid="mystery-claim-btn"
          >
            <Sparkles className="h-4 w-4" />
            {t("mystery.claim_btn", "Voir mon cadeau")}
          </Button>
        </>
      ) : (
        <>
          <div className="mt-5 text-xs uppercase tracking-[0.4em] font-bold text-red-300">
            {t("mystery.lose_eyebrow", "Boîte vide")}
          </div>
          <p className="mt-2 text-[13px] text-white/65 px-2">
            {t(
              "mystery.lose_text",
              "Le code ne correspond à aucun lot dans cette boîte. Tente ta chance lors d'un prochain jeu !",
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={onRetry}
            className="mt-5 rounded-xl"
            data-testid="mystery-retry-btn"
          >
            {t("mystery.back_to_email", "Retour")}
          </Button>
        </>
      )}
    </motion.div>
  );
}
